import { useMedplum } from '@medplum/react-hooks';
import {
  Alert,
  Anchor,
  Box,
  Button,
  Center,
  Divider,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { IconAlertCircle, IconBrandAuth0, IconLogin } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { config } from '../../config';
import { usePermissions } from '../../contexts/PermissionContext';
import { extractSignInErrorMessage, GENERIC_SIGN_IN_FAILURE, isInactiveProfileSignInError } from '../../lib/authLoginErrors';
import { isInteractiveUserProfile } from '../../lib/isInteractiveMedplumProfile';
import { signInFormSchema, type SignInFormValues } from '../../schemas/auth.schema';

const PILOT_EMAIL_SUFFIX = '@unify-test.local';

/**
 * OAuth scopes for sign-in. `offline_access` is required for Medplum to issue a
 * refresh token; without it the SDK can't silently renew the access token and
 * users are bounced to /signin once the ~1h access token expires.
 */
export const NATIVE_LOGIN_SCOPE = 'openid profile offline_access';
export const AUTH0_LOGIN_SCOPE = 'openid profile email offline_access';

function isLocalMedplumApi(): boolean {
  return /localhost|127\.0\.0\.1/.test(config.medplum.baseUrl);
}

function medplumLoginContextMissing(): boolean {
  return !config.medplum.clientId && !config.medplum.projectId;
}

function formatNativeLoginError(err: unknown): string {
  const raw = extractSignInErrorMessage(err) || 'Failed to sign in';
  if (isInactiveProfileSignInError(raw)) {
    return GENERIC_SIGN_IN_FAILURE;
  }
  const msg = raw;
  if (msg === 'Email or password is invalid' && isLocalMedplumApi()) {
    return (
      'Email or password is invalid. Use the exact pilot email, password = RBAC_PILOT_USER_PASSWORD from ' +
      'rbac:pilot:seed (default medplum123), and matching VITE_MEDPLUM_PROJECT_ID / CLIENT_ID. ' +
      'Re-run npm run rbac:pilot:seed — if the script’s pilot login step fails, fix that before Unify will work.'
    );
  }
  if (msg !== 'User not found') {
    return msg;
  }
  if (!isLocalMedplumApi()) {
    return msg;
  }
  if (medplumLoginContextMissing()) {
    return 'User not found. Pilot accounts are tied to a project — set VITE_MEDPLUM_CLIENT_ID or VITE_MEDPLUM_PROJECT_ID to the values printed by npm run rbac:pilot:seed, then restart Vite.';
  }
  return 'User not found. Check the email under Project → Users in the Medplum admin app, password (RBAC_PILOT_USER_PASSWORD), and that your VITE_* ids match that project.';
}

/**
 * Sign-in page supporting both Medplum native auth and Auth0
 *
 * Native auth flow:
 * 1. User enters email/password
 * 2. App calls medplum.startLogin() to get login context
 * 3. App calls medplum.processCode() to complete authentication
 *
 * Auth0 flow (optional):
 * 1. User clicks "Sign in with Auth0"
 * 2. App calls medplum.signInWithExternalAuth() which redirects to Auth0
 * 3. Auth0 authenticates user and redirects to Medplum
 * 4. Medplum creates/links user and redirects back to app with code
 */
export function SignInPage(): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if Auth0 is configured. Temporarily hidden in production: the Auth0 flow is broken there.
  const auth0Enabled = Boolean(config.auth0.domain && config.auth0.clientId) && !config.isProduction;

  const { isClient } = usePermissions();
  const postLoginRoute = isClient ? '/client-portal' : '/dashboard';
  const returnTo = searchParams.get('returnTo');
  const afterLoginRoute =
    returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : postLoginRoute;

  const form = useForm<SignInFormValues>({
    initialValues: {
      email: '',
      password: '',
    },
    validate: zod4Resolver(signInFormSchema),
  });

  // If a real user is already authenticated, redirect (ignore ClientApplication from intake client_credentials)
  useEffect(() => {
    if (isInteractiveUserProfile(medplum.getProfile())) {
      navigate(afterLoginRoute);
    }
  }, [medplum, navigate, afterLoginRoute]);

  // Handle native Medplum email/password sign-in
  const handleNativeSignIn = useCallback(
    async (values: SignInFormValues) => {
      setLoading(true);
      setError(null);

      try {
        const email = values.email.trim().toLowerCase();
        if (email.endsWith(PILOT_EMAIL_SUFFIX) && medplumLoginContextMissing()) {
          setError(
            'Pilot users are project-scoped in Medplum. Set VITE_MEDPLUM_CLIENT_ID or VITE_MEDPLUM_PROJECT_ID in .env (values from npm run rbac:pilot:seed), restart the dev server, then try again.',
          );
          return;
        }

        // startLogin() runs ensureCodeChallenge internally: if you set a fake codeChallenge,
        // PKCE is skipped and no codeVerifier is stored — token exchange then fails.
        const loginParams = {
          email,
          password: values.password,
          scope: NATIVE_LOGIN_SCOPE,
          ...(config.medplum.projectId ? { projectId: config.medplum.projectId } : {}),
          ...(config.medplum.clientId ? { clientId: config.medplum.clientId } : {}),
        } satisfies Parameters<typeof medplum.startLogin>[0];

        const loginResponse = await medplum.startLogin(loginParams);

        if (loginResponse.code) {
          await medplum.processCode(loginResponse.code, loginParams);
          navigate(afterLoginRoute);
        } else if (loginResponse.memberships) {
          const membership = loginResponse.memberships[0];
          const profileResponse = await medplum.post('auth/profile', {
            login: loginResponse.login,
            profile: membership.profile.reference,
          });
          if (profileResponse.code) {
            await medplum.processCode(profileResponse.code, loginParams);
            navigate(afterLoginRoute);
          }
        }
      } catch (err) {
        console.error('Sign in error:', err);
        setError(formatNativeLoginError(err));
      } finally {
        setLoading(false);
      }
    },
    [medplum, navigate, afterLoginRoute]
  );

  // Handle Auth0 external auth sign-in
  const handleAuth0SignIn = (): void => {
    if (!config.medplum.clientId || !config.medplum.projectId) {
      setError('Medplum client ID and project ID are required');
      return;
    }

    medplum.signInWithExternalAuth(
      config.auth0.authorizeUrl,
      config.auth0.clientId,
      `${config.medplum.baseUrl}auth/external`,
      {
        projectId: config.medplum.projectId,
        clientId: config.medplum.clientId,
        redirectUri: config.app.callbackUrl,
        scope: AUTH0_LOGIN_SCOPE,
      },
      // Auth0 RWA token exchange uses client_secret (set on the Medplum ClientApplication's
      // identityProvider). The verifier lives in browser localStorage and isn't reachable from
      // Medplum's /auth/external, so leaving PKCE on causes Auth0 to fail the exchange with
      // "Parameter 'code_verifier' is required". Pair with `pkceOptional: true` on the
      // Medplum SPA ClientApplication. See medplum.com/docs/auth/external-identity-providers.
      false
    );
  };

  return (
    <Box
      bg="unifyGold.0"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        minHeight: '100dvh',
        boxSizing: 'border-box',
      }}
    >
      <Center h="100%" p="md">
        <Paper shadow="md" p="lg" radius="md" w={400} maw="calc(100vw - 2rem)" bg="white">
          <Stack gap="md">
            <Box ta="center">
              <Title order={2} c="unifyGold.6">
                Unify EHR
              </Title>
              <Text c="dimmed" size="sm" mt="xs">
                Mental Health Clinical Platform
              </Text>
            </Box>

            {error && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
                {error}
              </Alert>
            )}

            <form onSubmit={form.onSubmit(handleNativeSignIn)}>
              <Stack gap="sm">
                <TextInput
                  label="Email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  {...form.getInputProps('email')}
                />
                <PasswordInput
                  label="Password"
                  placeholder="Your password"
                  autoComplete="current-password"
                  {...form.getInputProps('password')}
                />
                <Anchor
                  component={Link}
                  to="/forgot-password"
                  size="sm"
                  ta="right"
                  style={{ display: 'block', marginTop: '-4px' }}
                >
                  Forgot password?
                </Anchor>
                <Button
                  type="submit"
                  fullWidth
                  size="md"
                  loading={loading}
                  leftSection={<IconLogin size={20} />}
                >
                  Sign in
                </Button>
              </Stack>
            </form>

            {auth0Enabled && (
              <>
                <Divider label="OR" labelPosition="center" />
                <Button
                  fullWidth
                  size="md"
                  variant="outline"
                  leftSection={<IconBrandAuth0 size={20} />}
                  onClick={handleAuth0SignIn}
                >
                  Sign in with Auth0
                </Button>
              </>
            )}

            <Text size="xs" c="dimmed" ta="center">
              By signing in, you agree to comply with HIPAA regulations and protect patient privacy.
            </Text>
          </Stack>
        </Paper>
      </Center>
    </Box>
  );
}
